import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import type { ColumnQueryColumn } from '../components/common/columnQuery';
import { workedMinutes, type TimeEntry } from '@/data/scheduleTimeTracking';

dayjs.extend(utc);
dayjs.extend(timezone);

export const lunchLabel = (entry: TimeEntry) =>
  entry.lunch_minutes == null ? '—' : entry.lunch_minutes === 0 ? 'None' : `${entry.lunch_minutes} min`;
export const hoursLabel = (entry: TimeEntry) => {
  const minutes = workedMinutes(entry);
  return minutes == null ? '—' : (minutes / 60).toFixed(2);
};
export const allocationLabel = (entry: TimeEntry) => entry.job_kind === 'installation'
  ? entry.allocation_label || 'Needs review' : '—';

// Time filters group by day so a filter picks dates, not individual seconds.
const day = (value: string, timeZone: string) => dayjs(value).tz(timeZone).format('MMM D, YYYY');
const person: ColumnQueryColumn<TimeEntry> = {
  key: 'person', label: 'Person', filterValue: entry => entry.person_name || '—', sortValue: entry => entry.person_name.toLowerCase(),
};
const timeIn = (timeZone: string): ColumnQueryColumn<TimeEntry> => ({
  key: 'in', label: 'Time in', filterValue: entry => day(entry.clock_in, timeZone), sortValue: entry => Date.parse(entry.clock_in),
});

export function recordedTimeColumns(timeZone: string): ColumnQueryColumn<TimeEntry>[] {
  return [
    person,
    { key: 'job', label: 'Job', filterValue: entry => entry.job_code || '—', sortValue: entry => entry.job_code.toLowerCase() },
    timeIn(timeZone),
    {
      key: 'out', label: 'Time out',
      filterValue: entry => entry.clock_out ? day(entry.clock_out, timeZone) : 'Clocked in',
      // Open clocks sort after every finished one.
      sortValue: entry => entry.clock_out ? Date.parse(entry.clock_out) : Number.MAX_SAFE_INTEGER,
    },
    { key: 'lunch', label: 'Lunch', filterValue: lunchLabel, sortValue: entry => entry.lunch_minutes ?? -1 },
    { key: 'hours', label: 'Hours', align: 'right', sortValue: entry => workedMinutes(entry) ?? -1 },
    { key: 'allocation', label: 'Work type', filterValue: allocationLabel, sortValue: allocationLabel },
  ];
}

export function clockedInColumns(timeZone: string): ColumnQueryColumn<TimeEntry>[] {
  return [
    person,
    {
      key: 'job', label: 'Job', filterValue: entry => `${entry.job_code} · ${entry.job_name}`,
      sortValue: entry => `${entry.job_code} ${entry.job_name}`.toLowerCase(),
    },
    timeIn(timeZone),
    // Elapsed ticks every second, so it sorts (longest first = earliest clock-in) but does not filter.
    { key: 'elapsed', label: 'Elapsed', sortValue: entry => -Date.parse(entry.clock_in) },
  ];
}
